import { api } from "./client";
import type {
  SinglePresignRequest,
  InitMultipartRequest,
  InitMultipartResponse,
  PartUrlRequest,
  CompleteMultipartRequest,
} from "@titancloud/shared";

export async function presignSingleUpload(body: SinglePresignRequest): Promise<{
  fileId: string;
  uploadUrl: string;
  s3Key: string;
}> {
  return api.post("/upload/single/presign", body);
}

export async function confirmSingleUpload(fileId: string): Promise<void> {
  await api.post("/upload/confirm", { fileId });
}

export async function initMultipartUpload(
  body: InitMultipartRequest
): Promise<InitMultipartResponse> {
  return api.post("/upload/multipart/init", body);
}

export async function getPartPresignedUrl(body: PartUrlRequest): Promise<{
  uploadUrl: string;
  partNumber: number;
}> {
  return api.post("/upload/multipart/part-url", body);
}

export async function completeMultipartUpload(
  body: CompleteMultipartRequest
): Promise<void> {
  await api.post("/upload/multipart/complete", body);
}

export async function abortMultipartUpload(
  uploadId: string,
  fileId: string
): Promise<void> {
  await api.delete(`/upload/multipart/${uploadId}`, { json: { fileId } });
}

/** Upload a single file part directly to S3 via presigned URL */
export async function uploadPartToS3(
  presignedUrl: string,
  blob: Blob,
  onProgress?: (loaded: number, total: number) => void
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presignedUrl);

    if (onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) onProgress(e.loaded, e.total);
      });
    }

    xhr.onload = () => {
      if (xhr.status === 200) {
        const etag = xhr.getResponseHeader("ETag") ?? "";
        resolve(etag.replace(/"/g, ""));
      } else {
        reject(new Error(`Part upload failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error("Network error during part upload"));
    xhr.send(blob);
  });
}
